import { type MaybeRefOrGetter, toValue } from "vue";

import i18n from "@/plugins/i18n";
import { RouteRedispatchState, RouteStatus } from "@/constants/trip";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { CheckCallType } from "@/api/trips/dto/check-call";
import { useBaseView } from "@/composables/useBaseView";
import { isMonitorRouteTravelOrderInProgress } from "@/utils/routes";

import type { TripMonitorOrder, TripMonitorRoute, TripMonitorTravelOrder } from "@/api/trips/dto/trip-monitor";
import type { BrokerUpdateValidationContext, DisableRule } from "@/types/validation";

const t = i18n.global.t;

export function useBrokerUpdate() {
  function createMonitorBrokerUpdateContext(
    route: TripMonitorRoute,
    travelOrder: TripMonitorTravelOrder,
    orders: TripMonitorOrder[],
  ): BrokerUpdateValidationContext {
    const relatedOrder = orders.find((order) => order.id === travelOrder.orderId);

    return {
      sendUpdates: relatedOrder?.customer?.sendUpdates,
      routeStatus: route.status,
      redispatch: route.redispatch,
      isTechnicalPartial: route.technicalPartial,
      isTravelOrderInProgress: isMonitorRouteTravelOrderInProgress(route, travelOrder.id),
      checkCallType: route.lastCheckCall?.type,
      hasCheckCall: Boolean(route.lastCheckCall),
    };
  }

  function createBrokerUpdateStateDisableRules(
    context: MaybeRefOrGetter<BrokerUpdateValidationContext>,
  ): DisableRule[] {
    const {
      sendUpdates,
      routeStatus,
      redispatch,
      isTechnicalPartial,
      isTravelOrderInProgress,
      isCheckCallDismissed,
      checkCallType,
    } = toValue(context);

    return [
      {
        disabled: Boolean(isTechnicalPartial) && !isTravelOrderInProgress,
        reasons: [t("updates.disabled-reason.travel-order-not-in-progress")],
      },
      {
        disabled: redispatch === RouteRedispatchState.needed,
        reasons: [t("updates.disabled-reason.redispatch-needed")],
      },
      {
        disabled: routeStatus === RouteStatus.cancelled,
        reasons: [t("updates.disabled-reason.route-cancelled")],
      },
      {
        disabled: routeStatus === RouteStatus.planned && checkCallType !== CheckCallType.dispatchConfirmed,
        reasons: [t("updates.disabled-reason.route-planned")],
      },
      {
        disabled: !sendUpdates,
        reasons: [t("updates.disabled-reason.broker-updates-off")],
      },
      {
        disabled: Boolean(isCheckCallDismissed),
        reasons: [t("updates.disabled-reason.check-call-dismissed")],
      },
    ];
  }

  function createBrokerUpdateDisableRules(context: MaybeRefOrGetter<BrokerUpdateValidationContext>): DisableRule[] {
    const { hasAccess } = useBaseView();

    return [
      {
        disabled: !hasAccess(DispatcherPermissionValue.BrokerUpdatesEdit),
        reasons: [t("permission.create-broker-update")],
      },
      ...createBrokerUpdateStateDisableRules(context),
    ];
  }

  function isBrokerUpdateTimerDisabled(context: MaybeRefOrGetter<BrokerUpdateValidationContext>): boolean {
    return createBrokerUpdateStateDisableRules(context).some((rule) => rule.disabled);
  }

  function createSendUpdateDisableRules(context: MaybeRefOrGetter<BrokerUpdateValidationContext>): DisableRule[] {
    const { hasCheckCall } = toValue(context);

    return [
      {
        disabled: !hasCheckCall,
        reasons: [t("updates.disabled-reason.no-check-call")],
      },
      ...createBrokerUpdateDisableRules(context),
    ];
  }

  return {
    createMonitorBrokerUpdateContext,
    createBrokerUpdateDisableRules,
    createSendUpdateDisableRules,
    isBrokerUpdateTimerDisabled,
  };
}
